// src/hooks/usePropertyScraping.ts
/**
 * Property Scraping Hooks
 * Manages the property listing scraping workflow with reactive status tracking
 */

import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { type Id } from '../../convex/_generated/dataModel';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to track a single property listing with reactive status updates
 *
 * @param propertyId - The property listing ID to track, or undefined to skip
 * @returns Property data and status flags
 *
 * @example
 * ```tsx
 * const { property, isReady, isFailed, errorMessage } = usePropertyListing(propertyId);
 *
 * if (isReady) {
 *   // Property is ready for video generation
 * }
 * ```
 */
function usePropertyListing(propertyId: Id<'propertyListings'> | undefined) {
  const property = useQuery(
    api.property.queries.getPropertyListing,
    propertyId ? { propertyId } : 'skip'
  );

  const status = property?.status ?? null;

  const isScraping = status === 'scraping';
  const isAnalyzing = status === 'analyzing_location';
  const isGenerating = status === 'generating_description';
  const isReady = status === 'ready';
  const isFailed = status === 'failed';

  return {
    property,
    isLoading: property === undefined,
    isScraping,
    isAnalyzing,
    isGenerating,
    isReady,
    isFailed,
    status,
    errorMessage: property?.errorMessage ?? null,
  };
}

// ============================================================================
// Action Hooks
// ============================================================================

/**
 * Hook providing actions for the property scraping workflow
 *
 * @returns Object containing mutation and action functions
 *
 * @example
 * ```tsx
 * const { createListing, scrapeProperty } = usePropertyScrapingActions();
 *
 * // Create listing first
 * const propertyId = await createListing({ sourceUrl: url });
 *
 * // Then trigger scraping
 * const result = await scrapeProperty({ propertyId, url });
 * ```
 */
function usePropertyScrapingActions() {
  const createListing = useMutation(api.property.mutations.createPropertyListing);
  const scrapeProperty = useAction(api.property.scraping.scrapePropertyListing);
  const analyzeLocation = useAction(api.property.locationAnalysis.analyzeLocation);
  const generateDescription = useAction(
    api.property.descriptionGenerator.generatePropertyDescription
  );
  const deleteListing = useMutation(api.property.mutations.deletePropertyListing);
  const updateProperty = useMutation(api.property.mutations.updateProperty);

  return {
    createListing,
    scrapeProperty,
    analyzeLocation,
    generateDescription,
    deleteListing,
    updateProperty,
  };
}

// ============================================================================
// Combined Workflow Hook
// ============================================================================

/**
 * Combined hook for the full property scraping workflow
 *
 * Provides convenient `startScraping` function that handles the full workflow:
 * 1. Creates a property listing record
 * 2. Triggers the scraping action
 *
 * The scraping action will automatically:
 * - Update status to 'analyzing_location' or 'generating_description' on success
 * - Trigger location analysis if coordinates are found
 * - Update status to 'failed' on error
 *
 * @param propertyId - Optional property ID to track (for existing properties)
 * @returns Combined state and actions for the workflow
 *
 * @example
 * ```tsx
 * function PropertyScrapingForm() {
 *   const [currentPropertyId, setCurrentPropertyId] = useState<Id<'propertyListings'>>();
 *   const {
 *     property,
 *     isReady,
 *     isScraping,
 *     isAnalyzing,
 *     isGenerating,
 *     startScraping,
 *     isScrapingInProgress,
 *   } = usePropertyScrapingWorkflow(currentPropertyId);
 *
 *   const handleSubmit = async (url: string) => {
 *     const { propertyId } = await startScraping(url);
 *     setCurrentPropertyId(propertyId);
 *   };
 *
 *   return (
 *     <div>
 *       <PropertyUrlInput onSubmit={handleSubmit} isLoading={isScrapingInProgress} />
 *       {property && <PropertyStatus property={property} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePropertyScrapingWorkflow(propertyId?: Id<'propertyListings'>) {
  // Get property listing state
  const {
    property,
    isLoading,
    isScraping,
    isAnalyzing,
    isGenerating,
    isReady,
    isFailed,
    status,
    errorMessage,
  } = usePropertyListing(propertyId);

  // Get actions
  const {
    createListing,
    scrapeProperty,
    analyzeLocation,
    generateDescription,
    deleteListing,
    updateProperty,
  } = usePropertyScrapingActions();

  // Track if scraping is in progress (includes all processing stages)
  const isScrapingInProgress = isScraping || isAnalyzing || isGenerating;

  /**
   * Start the full scraping workflow
   * Creates a property listing and triggers scraping
   */
  const startScraping = async (url: string) => {
    // Step 1: Create the property listing record
    const newPropertyId = await createListing({ sourceUrl: url });

    // Step 2: Trigger the scraping action (fire and forget - status updates reactively)
    // We don't await this because the status will update reactively via useQuery
    scrapeProperty({ propertyId: newPropertyId, url }).catch((error: unknown) => {
      // Log error but don't throw - the property status will be updated to 'failed'
      console.error('[usePropertyScrapingWorkflow] Scraping failed:', error);
    });

    return { propertyId: newPropertyId };
  };

  return {
    // Property state
    property,
    isLoading,
    isScraping,
    isAnalyzing,
    isGenerating,
    isReady,
    isFailed,
    status,
    errorMessage,

    // Actions
    createListing,
    scrapeProperty,
    analyzeLocation,
    generateDescription,
    deleteListing,
    updateProperty,

    // Workflow helpers
    startScraping,
    isScrapingInProgress,
  };
}
